/**
 * Sidebar Navigation Component
 *
 * Korat Health Style: Government Modern with brand identity
 */

import React from 'react'
import {
  LayoutDashboard,
  Users,
  Wallet,
  Heart,
  Target,
  Accessibility,
  UserCircle,
  ChevronLeft,
  Moon,
  Info,
  LogOut,
  User,
} from 'lucide-react'

interface SidebarProps {
  currentPage?: string
  onNavigate?: (page: string) => void
}

interface NavItemProps {
  icon: React.ElementType
  label: string
  active?: boolean
  small?: boolean
  onClick?: () => void
}

function NavItem({ icon: Icon, label, active, small, onClick }: NavItemProps) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-3 rounded-lg text-sm transition-all
        ${
          active
            ? 'bg-korat-navy-50 text-korat-navy-700 font-semibold border-l-4 border-korat-navy-700 pl-2.5'
            : 'text-slate-600 hover:bg-slate-50'
        }
        ${small ? 'py-2' : 'py-2.5'}
      `}
    >
      <Icon className={small ? 'w-4 h-4' : 'w-5 h-5'} aria-hidden />
      <span>{label}</span>
    </button>
  )
}

export function Sidebar({ currentPage = 'dashboard', onNavigate }: SidebarProps) {
  const handleNavigate = (page: string) => {
    if (onNavigate) {
      onNavigate(page)
    } else {
      window.location.hash = page
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    window.location.hash = 'login'
  }

  // Get user from localStorage
  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null

  return (
    <aside className="w-64 bg-white border-r border-slate-200 flex flex-col shrink-0">
      {/* Logo Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-start gap-3">
          {/* Logo Placeholder */}
          <div className="w-12 h-12 bg-korat-navy-700 rounded-lg flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-lg">ก</span>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-korat-navy-700 leading-tight">
              องค์การบริหารส่วนจังหวัดนครราชสีมา
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Health Information & Report
            </p>
            <p className="text-xs font-medium text-slate-400 mt-1">Dashboard</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <NavItem
          icon={LayoutDashboard}
          label="ข้อมูลพื้นฐาน"
          active={currentPage === 'dashboard'}
          onClick={() => handleNavigate('dashboard')}
        />
        <NavItem
          icon={Users}
          label="บุคลากร"
          active={currentPage === 'personnel'}
          onClick={() => handleNavigate('personnel')}
        />
        <NavItem
          icon={Wallet}
          label="สถานะการเงิน"
          active={currentPage === 'financial'}
          onClick={() => handleNavigate('financial')}
        />
        <NavItem
          icon={Heart}
          label="สถานะสุขภาพ"
          active={currentPage === 'health'}
          onClick={() => handleNavigate('health')}
        />
        <NavItem
          icon={Target}
          label="ยุทธศาสตร์ทั้ง 7"
          active={currentPage === 'strategic'}
          onClick={() => handleNavigate('strategic')}
        />
        <NavItem
          icon={Accessibility}
          label="กองทุนฟื้นฟูสมรรถภาพ"
          active={currentPage === 'rehab'}
          onClick={() => handleNavigate('rehab')}
        />
        <NavItem
          icon={Heart}
          label="กลุ่มเปราะบาง"
          active={currentPage === 'vulnerable'}
          onClick={() => handleNavigate('vulnerable')}
        />
        <NavItem
          icon={UserCircle}
          label="โครงสร้างประชากร"
          active={currentPage === 'population'}
          onClick={() => handleNavigate('population')}
        />
      </nav>

      {/* User Info */}
      {user && (
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <div className="w-10 h-10 bg-korat-navy-700 rounded-full flex items-center justify-center shrink-0">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-slate-500 capitalize">{user.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>ออกจากระบบ</span>
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-slate-200 space-y-1">
        <NavItem icon={ChevronLeft} label="ย่อขนาดเมนู" small />
        <NavItem icon={Moon} label="เปลี่ยนธีม" small />
        <NavItem icon={Info} label="ข้อมูลติดต่อ" small />
      </div>
    </aside>
  )
}
